(() => {
  const cache = new Map();
  const normal = value => String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const aliases = model => {
    const result = new Set();
    [model.name, model.code].filter(Boolean).forEach(value => {
      const full = normal(value);
      result.add(full);
      let short = full.replace(/^(HUSQVARNAVIKING|SEWINGMACHINEPARTSMANUFACTURER|BABYLOCK|BROTHER|JANOME|PFAFF|JUKI|POOLIN|FEIYUE|ELNA)/, '');
      result.add(short);
      short = short.replace(/^HORIZON/, '').replace(/^MEMORYCRAFT/, 'MC').replace(/^INNOVIS/, '');
      result.add(short);
      if (/^MC\d/.test(short)) result.add(short.slice(2));
      if (/^NV\d/.test(short)) result.add(short.slice(2));
      if (/^HZL[A-Z0-9]/.test(short)) result.add(short.slice(3));
    });
    result.delete('');
    return result;
  };
  const modelObject = value => typeof value === 'string' ? { name: value } : value;
  const safeProduct = value => {
    try { const url = new URL(value, location.origin); return url.origin === location.origin && url.pathname.includes('/products/') ? url.pathname : null; } catch { return null; }
  };
  async function load(url) {
    if (cache.has(url)) return cache.get(url);
    const promise = (async () => {
      async function page(number) {
        const target = new URL(url, location.origin);
        target.searchParams.set('view', 'fm-fit-index');
        target.searchParams.set('page', String(number));
        const response = await fetch(target, { credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw new Error('Fit index unavailable');
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const script = document.querySelector('[data-fm-fit-index]');
        if (!script) throw new Error('Fit index missing');
        const data = JSON.parse(script.textContent);
        if (!Array.isArray(data.products) || !Number.isInteger(data.pages) || data.pages > 100) throw new Error('Invalid fit index');
        return data;
      }
      const first = await page(1);
      let next = 2;
      const products = [...first.products];
      async function worker() {
        while (next <= first.pages) {
          const current = next++;
          const result = await page(current);
          products.push(...result.products);
        }
      }
      await Promise.all([worker(), worker()]);
      const models = new Map();
      products.forEach(product => {
        product.url = safeProduct(product.url);
        if (!product.url) return;
        if (['unconfirmed', 'excluded', 'needs_confirmation'].includes(product.status)) return;
        const general = Array.isArray(product.models) ? product.models.map(modelObject) : [];
        if (product.html) {
          const document = new DOMParser().parseFromString(product.html, 'text/html');
          if (!product.notes) product.notes = [...document.querySelectorAll('p')].map(p => p.textContent.trim()).filter(Boolean).join('\n');
          document.querySelectorAll('ul li').forEach(li => {
            const note = li.querySelector('span');
            if (note) note.remove();
            const link = li.querySelector('a');
            general.push({ name: li.textContent.trim(), handle: link?.getAttribute('href')?.split('/products/')[1]?.split(/[?#]/)[0] });
          });
        }
        const add = (model, variant) => {
          if (!model?.name) return;
          const key = normal(model.name);
          if (!models.has(key)) models.set(key, { ...model, aliases: aliases(model), products: new Map() });
          const entry = models.get(key);
          if (!entry.handle && model.handle) entry.handle = model.handle;
          if (!entry.products.has(product.handle)) entry.products.set(product.handle, { product, variants: [] });
          if (variant) entry.products.get(product.handle).variants.push(variant);
        };
        general.forEach(model => add(model));
        (product.variants || []).forEach(variant => (variant.models || []).map(modelObject).forEach(model => add(model, variant)));
      });
      return { products, models: [...models.values()].sort((a, b) => a.name.localeCompare(b.name, 'en', { numeric: true })) };
    })();
    cache.set(url, promise);
    promise.catch(() => cache.delete(url));
    return promise;
  }
  if (customElements.get('fm-parts-finder')) return;
  customElements.define('fm-parts-finder', class extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.labels = JSON.parse(this.querySelector('[data-finder-labels]').textContent);
      this.form = this.querySelector('[data-model-form]');
      this.input = this.form.querySelector('[name="q"]');
      this.status = this.querySelector('[data-finder-status]');
      this.suggestions = this.querySelector('[data-finder-suggestions]');
      this.results = this.querySelector('[data-finder-results]');
      this.input.addEventListener('focus', () => this.prepare().catch(() => {}));
      this.input.addEventListener('input', () => {
        this.results.hidden = true;
        if (this.data) this.status.textContent = this.labels.ready;
        const url = new URL(location.href); url.searchParams.delete('fm_model'); history.replaceState(null, '', url.pathname + url.search + url.hash);
        this.setLinksModel('');
        clearTimeout(this.timer); this.timer = setTimeout(() => this.suggest(), 180);
      });
      this.form.addEventListener('submit', async event => {
        event.preventDefault();
        if (!this.input.value.trim()) return;
        try { await this.prepare(); this.search(); } catch { this.failed(); }
      });
      const query = new URL(location.href).searchParams.get('fm_model');
      if (query) { this.input.value = query; this.prepare().then(() => this.search()).catch(() => this.failed()); }
    }
    async prepare() {
      if (this.data) return this.data;
      this.status.textContent = this.labels.loading;
      try {
        this.data = await load(this.dataset.url);
        this.status.textContent = this.labels.ready;
        this.suggest();
        return this.data;
      } catch (error) { this.failed(); throw error; }
    }
    failed() {
      this.status.textContent = this.labels.error;
      this.results.hidden = false;
      this.results.replaceChildren(this.searchLink());
    }
    searchLink() {
      const link = document.createElement('a');
      const url = new URL(this.labels.search_url, location.origin);
      const brand = this.dataset.brand;
      let query = this.input.value.trim();
      if (brand && !normal(query).includes(normal(brand))) query = brand + ' ' + query;
      url.searchParams.set('q', query);
      url.searchParams.set('type', 'product');
      link.href = url.pathname + url.search;
      link.textContent = this.labels.general_search;
      return link;
    }
    suggest() {
      this.suggestions.replaceChildren();
      if (!this.data) { if (this.input.value.length > 1) this.prepare().catch(() => {}); return; }
      const query = normal(this.input.value);
      if (query.length < 2) return;
      const choices = this.data.models.filter(model => [...model.aliases].some(alias => alias.includes(query))).slice(0, 8);
      choices.forEach(model => {
        const li = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button'; button.textContent = model.name;
        button.addEventListener('click', () => { this.input.value = model.name; this.showModel(model); });
        li.append(button); this.suggestions.append(li);
      });
    }
    search() {
      const query = normal(this.input.value);
      const exact = this.data.models.filter(model => model.aliases.has(query));
      if (exact.length === 1) { this.showModel(exact[0]); return; }
      const references = this.data.products.filter(product => product.url && (String(product.part || '').split(/[,;/|]+/).some(part => normal(part) === query) || (product.variants || []).some(variant => normal(variant.sku) === query)));
      if (references.length) {
        this.showResults(this.labels.reference, references.map(product => ({ product, variants: [] }))); return;
      }
      this.results.hidden = false;
      this.results.replaceChildren();
      this.status.textContent = this.labels.no_match;
      this.results.append(this.searchLink());
      this.suggest();
    }
    showModel(model) {
      this.showResults(this.labels.matches + ' ' + model.name, [...model.products.values()], model);
      const url = new URL(location.href);
      url.searchParams.set('fm_model', model.name);
      history.replaceState(null, '', url.pathname + url.search + url.hash);
      this.setLinksModel(model.name);
      this.suggestions.replaceChildren();
    }
    setLinksModel(name) {
      this.closest('.fm-brand-hub').querySelectorAll('.fm-brand-hub__category').forEach(link => {
        const url = new URL(link.href, location.origin);
        if (url.origin !== location.origin) return;
        if (name) url.searchParams.set('fm_model', name); else url.searchParams.delete('fm_model');
        link.href = url.pathname + url.search;
      });
    }
    showResults(heading, matches, model) {
      this.status.textContent = heading + ' (' + matches.length + ')';
      this.results.hidden = false;
      this.results.replaceChildren();
      if (model?.handle) {
        const href = safeProduct('/products/' + model.handle);
        if (href) { const link = document.createElement('a'); link.href = href; link.textContent = this.labels.machine + ' ' + model.name; this.results.append(link); }
      }
      const narrowLabel = document.createElement('label'); narrowLabel.textContent = this.labels.narrow_label;
      const narrow = document.createElement('input'); narrow.type = 'text'; narrow.placeholder = this.labels.narrow_placeholder; narrow.autocomplete = 'off'; narrowLabel.append(narrow); this.results.append(narrowLabel);
      const ul = document.createElement('ul'); ul.className = 'fm-model-finder__results';
      matches.forEach(({ product, variants }) => {
        const li = document.createElement('li'); li.className = 'fm-model-finder__result';
        if (product.image) { const image = document.createElement('img'); image.src = product.image; image.alt = product.title; image.width = 80; image.height = 80; image.loading = 'lazy'; li.append(image); }
        const detail = document.createElement('div');
        const h4 = document.createElement('h4');
        const link = document.createElement('a'); link.href = product.url; link.textContent = product.title; h4.append(link); detail.append(h4);
        if (product.part) { const p = document.createElement('p'); p.textContent = product.part; detail.append(p); }
        const price = document.createElement('p'); price.textContent = (product.varies ? this.labels.from + ' ' : '') + product.price + ' ' + this.labels.vat; detail.append(price);
        if (product.notes) { const p = document.createElement('p'); p.className = 'fm-model-finder__notes'; p.textContent = product.notes; detail.append(p); }
        variants.forEach(variant => { const p = document.createElement('p'); p.textContent = this.labels.variant + ' ' + (variant.label || variant.sku || '') + (variant.notes ? ' — ' + variant.notes : ''); detail.append(p); });
        const open = document.createElement('a'); open.href = product.url; open.textContent = this.labels.open + ' →'; detail.append(open); li.append(detail); ul.append(li);
      });
      this.results.append(ul);
      const empty = document.createElement('p'); empty.textContent = this.labels.narrow_none; empty.hidden = true; this.results.append(empty);
      narrow.addEventListener('input', () => {
        const query = normal(narrow.value); let visible = 0;
        [...ul.children].forEach(li => { const show = !query || normal(li.textContent).includes(query); li.hidden = !show; if (show) visible++; });
        this.status.textContent = heading + ' (' + visible + '/' + matches.length + ')'; empty.hidden = visible !== 0;
      });
      const actions = document.createElement('div'); actions.className = 'fm-model-finder__actions';
      const clear = document.createElement('button'); clear.type = 'button'; clear.textContent = this.labels.clear;
      clear.addEventListener('click', () => { this.results.hidden = true; this.results.replaceChildren(); this.input.value = ''; this.status.textContent = this.labels.ready; const url = new URL(location.href); url.searchParams.delete('fm_model'); history.replaceState(null, '', url.pathname + url.search + url.hash); this.setLinksModel(''); });
      actions.append(clear, this.searchLink()); this.results.append(actions);
    }
  });
})();
